/*
 * ANIME CRUSH — OVERLAY BEHAVIOUR
 *
 * The drawers and modals already paint correctly once ac-components.css pairs
 * their surfaces with their ink. What they do not do is behave. Measured on the
 * candidate, at 390, with the page scrolled to 600px:
 *
 *   nav drawer     focus stays on the header trigger, outside the drawer
 *   cart drawer    same
 *   search modal   focus moves to the input (correct), but not back on close
 *   all three      focus lands on <body> after close, so a keyboard user is
 *                  returned to the top of the document instead of their place
 *   product zoom   opening it scrolls the page to 0 and closing leaves it
 *                  there: the reading position is destroyed
 *   product zoom   clicking the backdrop does nothing
 *
 * ESC already closes all four, and the drawers already lock and restore
 * scrolling, so neither is re-implemented here.
 *
 * WHY AN OBSERVER AND NOT EVENT HOOKS
 * The theme opens these from its own bundle and emits no events. It does mark
 * state declaratively - `aria-hidden` on .Drawer and .Modal, `pswp--open` on
 * .pswp - so that is what this watches. Nothing here opens or closes an
 * overlay by itself; it only reacts to the theme having done so.
 */
(function () {
  'use strict';

  var TRIGGERS = '[data-action="open-drawer"],[data-action="open-modal"],' +
                 '[data-action="open-product-zoom"],.Product__SlideItem--image';
  var CLOSERS = '[data-action="close-drawer"],[data-action="close-modal"],' +
                '.Drawer__Close,.Modal__Close,.pswp__button--close';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([type="hidden"]),' +
                  'select,textarea,[tabindex]:not([tabindex="-1"])';

  var lastTrigger = null;
  var savedScroll = null;

  /*
   * Capture phase, so the reading position is recorded BEFORE the theme's own
   * handler runs and PhotoSwipe scrolls the document to 0.
   */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest(TRIGGERS) : null;
    if (!t) return;
    lastTrigger = t;
    savedScroll = window.scrollY;
  }, true);

  function visibleFocusables(box) {
    return Array.prototype.slice.call(box.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        var r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
      });
  }

  function focusSafely(el) {
    if (!el) return false;
    /* A trigger that is display:none at this breakpoint cannot take focus, and
       calling focus() on it silently drops focus to <body> - which looked like
       "focus return is broken" until the trigger turned out to be the hidden
       desktop copy of the same control. */
    if (!el.offsetParent && el !== document.body &&
        getComputedStyle(el).position !== 'fixed') return false;
    /* preventScroll matters: focusing a control inside a just-opened overlay
       otherwise scrolls the document underneath it. */
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
    return document.activeElement === el;
  }

  /*
   * PhotoSwipe binds its own tap handling rather than a click listener, so
   * calling .click() on .pswp__button--close does nothing - measured. Its
   * keyboard handler reads the legacy keyCode, so a synthetic Escape that
   * carries one closes it through the library's own path instead of ours.
   */
  function dispatchEscape() {
    var ke;
    try {
      ke = new KeyboardEvent('keydown', { bubbles: true, cancelable: true, key: 'Escape', code: 'Escape' });
    } catch (err) {
      ke = document.createEvent('Event');
      ke.initEvent('keydown', true, true);
      ke.key = 'Escape';
    }
    try {
      Object.defineProperty(ke, 'keyCode', { get: function () { return 27; } });
      Object.defineProperty(ke, 'which', { get: function () { return 27; } });
    } catch (err) { /* already defined; the key/code pair may still be enough */ }
    document.dispatchEvent(ke);
  }

  function onOpen(box) {
    if (box.contains(document.activeElement)) return;
    var closer = box.querySelector(CLOSERS);
    focusSafely(closer || visibleFocusables(box)[0]);
  }

  function onClose(box, isZoom) {
    /* PhotoSwipe leaves the document at 0. Put the customer back where they
       were reading before they opened the picture. */
    if (isZoom && savedScroll !== null && Math.abs(window.scrollY - savedScroll) > 2) {
      window.scrollTo(0, savedScroll);
    }
    if (lastTrigger && document.body.contains(lastTrigger)) focusSafely(lastTrigger);
  }

  /* Tab stays inside the overlay while it is open. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var open = document.querySelector('.Drawer[aria-hidden="false"],.Modal[aria-hidden="false"],.pswp.pswp--open');
    if (!open || !open.contains(document.activeElement)) return;
    var list = visibleFocusables(open);
    if (list.length < 2) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); focusSafely(last); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); focusSafely(first); }
  });

  /*
   * Backdrop click closes the picture viewer. Only the backdrop itself: a click
   * on .pswp__img is PhotoSwipe's own zoom gesture and must not close it.
   */
  document.addEventListener('click', function (e) {
    var pswp = document.querySelector('.pswp.pswp--open');
    if (!pswp) return;
    var t = e.target;
    if (!t.classList) return;
    if (t.closest('.pswp__button') || t.closest('.pswp__caption') || t.closest('.pswp__top-bar')) return;
    var isBackdrop = t.classList.contains('pswp__bg') ||
                     t.classList.contains('pswp__scroll-wrap') ||
                     t.classList.contains('pswp__container') ||
                     t.classList.contains('pswp__item');
    if (!isBackdrop) return;
    dispatchEscape();
  });

  function watch() {
    var boxes = Array.prototype.slice.call(
      document.querySelectorAll('.Drawer,.Modal,.pswp'));
    if (!boxes.length) return;

    var state = new WeakMap();
    function isOpen(box) {
      return box.classList.contains('pswp')
        ? box.classList.contains('pswp--open')
        : box.getAttribute('aria-hidden') === 'false';
    }
    boxes.forEach(function (b) { state.set(b, isOpen(b)); });

    var obs = new MutationObserver(function (records) {
      records.forEach(function (rec) {
        var box = rec.target;
        if (!state.has(box)) return;
        var now = isOpen(box);
        if (now === state.get(box)) return;
        state.set(box, now);
        var isZoom = box.classList.contains('pswp');
        /* Not one frame: the theme's own open handler runs after the attribute
           flips and puts focus back on the trigger, so a rAF-timed focus call
           is overwritten and the drawer opens with focus still outside it.
           This waits out the drawer transition instead. */
        if (now) setTimeout(function () { onOpen(box); }, 260);
        else onClose(box, isZoom);
      });
    });
    boxes.forEach(function (b) {
      obs.observe(b, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
    });
  }

  if (document.readyState !== 'loading') watch();
  else document.addEventListener('DOMContentLoaded', watch);
})();
